import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Notiflix from 'notiflix'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { FaSearch } from 'react-icons/fa'
import api from '../../../../../helpers/apiRequester'
import { useAuthUserData, useIsSentinelUser } from '../../../../../authUserProvider'
import { Button, CollapsibleSection, EmptyState, LoadingOverlay, PageHeader, notiflixDanger } from '../../../../../ui'
import {
    ClientData,
    ClientDocumentsResponse,
    SignatureEvidenceResponse,
    getFirstName,
    getFullName,
    getLastName,
    isValidEmail,
    isValidUKPhone,
    parseIsoDate,
    performClientSearch,
    toIsoDate,
} from '../shared'
import { ClientRecord } from './clientRecord'
import { SignatureEvidenceState } from './signatureEvidenceCard'
import { DocumentsState } from './documentsCard'
import { CreditReportForm, CreditReportModal } from './creditReportModal'
import { MatchesTable } from './matchesTable'
import { EMPTY_CRITERIA, SearchCriteria, SearchErrors, SearchPanel, describeCriteria } from './searchPanel'
import { NotesTab } from './notesTab'
import { ClientActionsTab } from './clientActionsTab'
import s from './styles.module.scss'

type SearchStatus = 'idle' | 'loading' | 'empty' | 'error' | 'results'

const EMPTY_DOCUMENTS: DocumentsState = {
    status: 'loading',
    documents: [],
    missing: [],
    claims: [],
    checkedLocations: 0,
}

const EMPTY_CREDIT_FORM: CreditReportForm = {
    firstname: '',
    lastname: '',
    email: '',
    telephone: '',
    dob: null,
    flat: '',
    house_name: '',
    house_number: '',
    street: '',
    town_city: '',
    county: '',
    postcode: '',
}

const isUnauthorised = (error: any) => error?.response?.status === 401 || error?.status === 401

const validate = (c: SearchCriteria): SearchErrors => {
    const errors: SearchErrors = {}
    const firstName = c.firstName.trim()
    const lastName = c.lastName.trim()
    const phone = c.phone.trim()
    const email = c.email.trim()

    if (!firstName && !lastName && !phone && !email) {
        errors.form = 'Enter a full name, phone number or email address to search.'
        return errors
    }
    if ((firstName || lastName) && !(firstName && lastName)) {
        if (!firstName) errors.firstName = 'First name is required when searching by name.'
        if (!lastName) errors.lastName = 'Last name is required when searching by name.'
    }
    if (email && !isValidEmail(email)) errors.email = 'Enter a valid email address.'
    if (phone && !isValidUKPhone(phone)) errors.phone = 'Enter a UK number with the +44 prefix, e.g. +447123456789.'
    return errors
}

export const SupportSearchView = () => {
    const navigate = useNavigate()
    const authUser = useAuthUserData()
    const canCancelClaims = useIsSentinelUser()
    const [searchParams, setSearchParams] = useSearchParams()
    const autosearchKickedOff = useRef(false)

    // Search -------------------------------------------------------------
    const [criteria, setCriteria] = useState<SearchCriteria>(EMPTY_CRITERIA)
    const [errors, setErrors] = useState<SearchErrors>({})
    const [status, setStatus] = useState<SearchStatus>('idle')
    const [isSearchOpen, setIsSearchOpen] = useState(true)
    const [searchResults, setSearchResults] = useState<ClientData[]>([])
    const [clientData, setClientData] = useState<ClientData | null>(null)
    const [lastSearched, setLastSearched] = useState<SearchCriteria | null>(null)

    // Selected client actions ---------------------------------------------
    const [isSendingEmail, setIsSendingEmail] = useState(false)
    const [creditReportUrl, setCreditReportUrl] = useState<string | null>(null)
    const [isLoadingCreditReportUrl, setIsLoadingCreditReportUrl] = useState(false)
    const [isRefreshingResults, setIsRefreshingResults] = useState(false)
    const [isFindingPcpAgreements, setIsFindingPcpAgreements] = useState(false)
    const [isRegeneratingCfa, setIsRegeneratingCfa] = useState(false)
    const [cfaUrl, setCfaUrl] = useState<string | null>(null)
    const [isCancellingClaim, setIsCancellingClaim] = useState<string | null>(null)
    const [signatureEvidence, setSignatureEvidence] = useState<SignatureEvidenceState>({
        status: 'loading',
        evidence: null,
    })
    // Bumped by "Retry" to re-run the signature evidence effect.
    const [signatureFetchKey, setSignatureFetchKey] = useState(0)
    const [documents, setDocuments] = useState<DocumentsState>(EMPTY_DOCUMENTS)
    // Bumped by "Retry" and after actions that write new files to S3.
    const [documentsFetchKey, setDocumentsFetchKey] = useState(0)

    // "Actions" button in the record hero opens the Account actions section and scrolls to it.
    const actionsRef = useRef<HTMLDivElement>(null)
    const [actionsOpen, setActionsOpen] = useState(false)
    const [scrollToActions, setScrollToActions] = useState(false)
    const jumpToActions = useCallback(() => {
        setActionsOpen(true)
        setScrollToActions(true)
    }, [])
    // Scroll once the opened section has committed, then again shortly after: the
    // actions body fetches its claim preview on mount and grows, which would
    // otherwise leave the first scroll clamped short of the section.
    useEffect(() => {
        if (!scrollToActions || !actionsOpen) return
        const scroll = () => actionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        scroll()
        // Not cleared on cleanup: resetting the flag below re-runs this effect immediately.
        window.setTimeout(scroll, 700)
        setScrollToActions(false)
    }, [scrollToActions, actionsOpen])

    // Credit report form ----------------------------------------------------
    const [showCreditForm, setShowCreditForm] = useState(false)
    const [isEditingCreditForm, setIsEditingCreditForm] = useState(false)
    const [isRerunningCreditReport, setIsRerunningCreditReport] = useState(false)
    const [creditForm, setCreditForm] = useState<CreditReportForm>(EMPTY_CREDIT_FORM)

    const selectClient = useCallback((client: ClientData) => {
        setClientData(client)
        setCreditReportUrl(null)
        setCfaUrl(null)
        setIsSearchOpen(false)
        setStatus('results')
    }, [])

    const runSearch = useCallback(
        async (next: SearchCriteria) => {
            const validation = validate(next)
            setErrors(validation)
            if (Object.keys(validation).length) return

            setStatus('loading')
            setClientData(null)
            setSearchResults([])
            setCfaUrl(null)
            setCreditReportUrl(null)
            setLastSearched(next)

            try {
                const result = await performClientSearch({
                    email: next.email.trim() || undefined,
                    phone: next.phone.trim() || undefined,
                    firstName: next.firstName.trim() || undefined,
                    lastName: next.lastName.trim() || undefined,
                    dob: toIsoDate(next.dob),
                })

                if (!result.success) {
                    setStatus('error')
                    return
                }
                if (result.totalResults === 1) {
                    selectClient(result.results[0])
                } else if (result.totalResults > 1) {
                    setSearchResults(result.results)
                    setIsSearchOpen(false)
                    setStatus('results')
                } else {
                    setStatus('empty')
                }
            } catch (error: any) {
                if (isUnauthorised(error)) {
                    Notiflix.Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                    navigate('/login')
                    return
                }
                setStatus('error')
            }
        },
        [navigate, selectClient],
    )

    // Prefill from ?email= / ?phone= (home page Client Search section) and search once.
    useEffect(() => {
        if (autosearchKickedOff.current) return
        const emailParam = searchParams.get('email') || ''
        const phoneParam = searchParams.get('phone') || ''
        if (!emailParam && !phoneParam) return

        autosearchKickedOff.current = true
        const next = { ...EMPTY_CRITERIA, email: emailParam, phone: phoneParam }
        setCriteria(next)

        const nextParams = new URLSearchParams(searchParams)
        nextParams.delete('email')
        nextParams.delete('phone')
        setSearchParams(nextParams, { replace: true })

        void runSearch(next)
    }, [searchParams, setSearchParams, runSearch])

    const updateCriteria = (patch: Partial<SearchCriteria>) => {
        setCriteria((prev) => ({ ...prev, ...patch }))
        // Clear the error for any field the user is now editing.
        setErrors((prev) => {
            if (!Object.keys(prev).length) return prev
            const next = { ...prev }
            delete next.form
            for (const key of Object.keys(patch) as (keyof SearchCriteria)[]) delete next[key]
            return next
        })
    }

    const startNewSearch = () => {
        setCriteria(EMPTY_CRITERIA)
        setErrors({})
        setStatus('idle')
        setSearchResults([])
        setClientData(null)
        setCreditReportUrl(null)
        setCfaUrl(null)
        setLastSearched(null)
        setIsSearchOpen(true)
    }

    const backToMatches = () => {
        setClientData(null)
        setCreditReportUrl(null)
        setCfaUrl(null)
    }

    // Re-run the last search after a server-side action and re-select the same client.
    const refreshSelectedClient = async () => {
        if (!lastSearched || !clientData) return
        const { client_id, origin } = clientData
        setIsRefreshingResults(true)
        try {
            const result = await performClientSearch({
                email: lastSearched.email.trim() || undefined,
                phone: lastSearched.phone.trim() || undefined,
                firstName: lastSearched.firstName.trim() || undefined,
                lastName: lastSearched.lastName.trim() || undefined,
                dob: toIsoDate(lastSearched.dob),
            })
            if (!result.success || result.totalResults === 0) return

            setSearchResults(result.totalResults > 1 ? result.results : [])
            const same = result.results.find((r) => r.client_id === client_id && r.origin === origin)
            // Clear first so the credit-report effect re-fetches for the refreshed record.
            setClientData(null)
            setCreditReportUrl(null)
            setTimeout(() => {
                if (same) setClientData(same)
                else if (result.totalResults === 1) setClientData(result.results[0])
            }, 0)
        } catch (error) {
            console.error('Error refreshing search results:', error)
        } finally {
            setIsRefreshingResults(false)
        }
    }

    // Credit report URL for the selected client ---------------------------------
    useEffect(() => {
        if (!clientData?.client_id || !clientData?.origin) {
            setCreditReportUrl(null)
            return
        }
        let cancelled = false
        const fetchCreditReportUrl = async () => {
            setIsLoadingCreditReportUrl(true)
            try {
                const response = await api({
                    url: 'get-credit-report-url',
                    data: { client_id: clientData.client_id, origin: clientData.origin },
                })
                if (cancelled) return
                setCreditReportUrl(
                    response.data.success && response.data.creditReportUrl ? response.data.creditReportUrl : null,
                )
            } catch (error: any) {
                if (cancelled) return
                if (isUnauthorised(error)) {
                    Notiflix.Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                    navigate('/login')
                    return
                }
                console.error('Error fetching credit report URL:', error)
                setCreditReportUrl(null)
            } finally {
                if (!cancelled) setIsLoadingCreditReportUrl(false)
            }
        }
        fetchCreditReportUrl()
        return () => {
            cancelled = true
        }
    }, [clientData?.client_id, clientData?.origin, navigate])

    // Signature evidence for a signed CFA ------------------------------------------
    // Point the lookup at the signed base claim when we can identify it; the
    // backend falls back to the client_id when the case_ref does not match.
    const signedClaimRef = (() => {
        if (!clientData?.cfa_signed) return null
        const withRef = (clientData.claims ?? []).filter((c) => c.case_ref && c.case_ref !== 'TBC')
        return (
            withRef.find((c) => c.signed && c.base_claim)?.case_ref ??
            withRef.find((c) => c.signed)?.case_ref ??
            withRef[0]?.case_ref ??
            null
        )
    })()
    useEffect(() => {
        if (!clientData?.client_id || !clientData?.origin || !clientData.cfa_signed) {
            setSignatureEvidence({ status: 'loading', evidence: null })
            return
        }
        let cancelled = false
        const fetchEvidence = async () => {
            setSignatureEvidence({ status: 'loading', evidence: null })
            try {
                const response = await api({
                    url: 'client-signature-evidence',
                    data: { client_id: clientData.client_id, origin: clientData.origin, case_ref: signedClaimRef },
                })
                if (cancelled) return
                const body: SignatureEvidenceResponse = response.data
                if (!body.success) {
                    setSignatureEvidence({ status: 'error', evidence: null, error: body.message })
                } else if (!body.signed || !body.evidence) {
                    setSignatureEvidence({ status: 'none', evidence: null })
                } else {
                    setSignatureEvidence({ status: 'ready', evidence: body.evidence })
                }
            } catch (error: any) {
                if (cancelled) return
                if (isUnauthorised(error)) {
                    Notiflix.Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                    navigate('/login')
                    return
                }
                console.error('Error fetching signature evidence:', error)
                setSignatureEvidence({
                    status: 'error',
                    evidence: null,
                    error: error?.response?.data?.message || error?.message,
                })
            }
        }
        fetchEvidence()
        return () => {
            cancelled = true
        }
    }, [clientData?.client_id, clientData?.origin, clientData?.cfa_signed, signedClaimRef, signatureFetchKey, navigate])

    // Documents on file for the selected client ---------------------------------------
    useEffect(() => {
        if (!clientData?.client_id || !clientData?.origin) {
            setDocuments(EMPTY_DOCUMENTS)
            return
        }
        let cancelled = false
        const fetchDocuments = async () => {
            setDocuments(EMPTY_DOCUMENTS)
            try {
                const response = await api({
                    url: 'client-documents',
                    data: { client_id: clientData.client_id, origin: clientData.origin },
                })
                if (cancelled) return
                const body: ClientDocumentsResponse = response.data
                if (!body.success) {
                    setDocuments({ ...EMPTY_DOCUMENTS, status: 'error', error: body.message })
                    return
                }
                setDocuments({
                    status: 'ready',
                    documents: body.documents ?? [],
                    missing: body.missing ?? [],
                    claims: body.claims ?? [],
                    checkedLocations: body.checked_locations ?? 0,
                })
            } catch (error: any) {
                if (cancelled) return
                if (isUnauthorised(error)) {
                    Notiflix.Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                    navigate('/login')
                    return
                }
                console.error('Error fetching client documents:', error)
                setDocuments({
                    ...EMPTY_DOCUMENTS,
                    status: 'error',
                    error: error?.response?.data?.message || error?.message,
                })
            }
        }
        fetchDocuments()
        return () => {
            cancelled = true
        }
    }, [clientData?.client_id, clientData?.origin, documentsFetchKey, navigate])

    // Credit report form --------------------------------------------------------
    const openCreditForm = () => {
        if (!clientData) return
        setCreditForm({
            firstname: clientData.firstname || '',
            lastname: clientData.lastname || '',
            email: clientData.email || '',
            telephone: clientData.telephone || '',
            dob: parseIsoDate(clientData.dob),
            flat: clientData.current_address?.flat || '',
            house_name: clientData.current_address?.house_name || '',
            house_number: clientData.current_address?.house_number || '',
            street: clientData.current_address?.street || '',
            town_city: clientData.current_address?.town_city || '',
            county: clientData.current_address?.county || '',
            postcode: clientData.current_address?.postcode || '',
        })
        setIsEditingCreditForm(false)
        setShowCreditForm(true)
    }

    const closeCreditForm = () => {
        setShowCreditForm(false)
        setIsEditingCreditForm(false)
    }

    const confirmRunCreditReport = () => {
        Confirm.show(
            creditReportUrl ? 'Re-run credit report?' : 'Run credit report?',
            'This will submit a new credit check for the client using the details shown.',
            creditReportUrl ? 'Re-run' : 'Run',
            'Cancel',
            () => void runCreditReport(),
            () => undefined,
        )
    }

    const runCreditReport = async () => {
        if (!clientData?.client_id || !clientData?.origin) {
            Notiflix.Report.failure('Error', 'Client information is missing. Please try again.', 'OK')
            return
        }
        setIsRerunningCreditReport(true)
        try {
            const { dob, ...rest } = creditForm
            const response = await api({
                url: 'rerun-credit-report',
                data: {
                    client_id: clientData.client_id,
                    origin: clientData.origin,
                    client: { ...rest, dob: toIsoDate(dob) || '' },
                },
            })

            if (response.data.success) {
                closeCreditForm()
                Notiflix.Report.success(
                    'Credit Report Requested',
                    'The credit check has been submitted. Refreshing the client record…',
                    'OK',
                    () => void refreshSelectedClient(),
                )
            } else {
                Notiflix.Report.failure(
                    'Credit Report Failed',
                    response.data.error || 'Failed to run the credit report. Please try again.',
                    'OK',
                )
            }
        } catch (error: any) {
            if (isUnauthorised(error)) {
                Notiflix.Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                navigate('/login')
                return
            }
            Report.failure(
                'Credit Report Error',
                'An error occurred whilst running the credit report. Please try again.',
                'OK',
            )
        } finally {
            setIsRerunningCreditReport(false)
        }
    }

    // Claims --------------------------------------------------------------------
    const markClaimCancelled = (caseRef: string, cancellationDate?: string) => {
        setClientData((prev) =>
            prev
                ? {
                      ...prev,
                      claims: prev.claims.map((claim) =>
                          claim.case_ref === caseRef
                              ? {
                                    ...claim,
                                    cancelled: true,
                                    cancellation_date: cancellationDate ?? claim.cancellation_date,
                                }
                              : claim,
                      ),
                  }
                : prev,
        )
    }

    // Actions panel → bulk/selective cancellation: sync the refreshed states onto the claims table.
    const handleActionsClaimsCancelled = (
        states: { case_ref: string; cancelled: boolean; cancellation_date: string | null }[],
    ) => {
        const byRef = new Map(states.map((st) => [st.case_ref, st]))
        setClientData((prev) =>
            prev
                ? {
                      ...prev,
                      claims: prev.claims.map((claim) => {
                          const state = byRef.get(claim.case_ref)
                          return state
                              ? {
                                    ...claim,
                                    cancelled: state.cancelled,
                                    cancellation_date: state.cancellation_date ?? claim.cancellation_date,
                                }
                              : claim
                      }),
                  }
                : prev,
        )
    }

    // Actions panel → client erased: drop it and fall back to the remaining matches.
    const handleActionsClientDeleted = () => {
        if (!clientData) return
        const remaining = searchResults.filter(
            (r) => !(r.client_id === clientData.client_id && r.origin === clientData.origin),
        )
        setSearchResults(remaining)
        setClientData(null)
        setCreditReportUrl(null)
        setCfaUrl(null)
        if (remaining.length === 0) {
            setStatus('idle')
            setIsSearchOpen(true)
        }
    }

    const handleCancelClaim = (caseRef: string) => {
        Confirm.show(
            'Cancel this claim?',
            'The case will be marked cancelled by the client and a cancellation email sent to them.',
            'Cancel claim',
            'Go back',
            async () => {
                setIsCancellingClaim(caseRef)
                try {
                    const { data } = await api({
                        url: '/actions/cancel',
                        data: {
                            case_ref: `${caseRef}|${clientData?.origin}`,
                            action: 'cancellation-letter',
                            reason: 'Client request',
                        },
                    })

                    if (data.success === true && data.cancelled === true) {
                        markClaimCancelled(caseRef, data.cancellation_date)
                        if (data.letter_sent === false) {
                            Notiflix.Report.warning(
                                'Case Cancelled',
                                'The case has been cancelled, but the client email did not send. Please re-send it manually.',
                                'OK',
                            )
                        } else {
                            Notiflix.Report.success('Case Cancelled', 'The case has been cancelled successfully.', 'OK')
                        }
                        return
                    }
                    if (data.code === 'already_cancelled') {
                        markClaimCancelled(caseRef, data.cancellation_date)
                        Notiflix.Report.info('Already Cancelled', 'This case was already cancelled.', 'OK')
                        return
                    }
                    Report.failure(
                        'Cancellation Failed',
                        data.message || 'Failed to cancel the case. Please try again.',
                        'OK',
                    )
                } catch (error: any) {
                    if (isUnauthorised(error)) {
                        Notiflix.Report.failure(
                            'Session Expired',
                            'Your session has expired. Please log in again.',
                            'OK',
                        )
                        navigate('/login')
                        return
                    }
                    const responseData = error?.response?.data
                    if (responseData?.code === 'already_cancelled') {
                        markClaimCancelled(caseRef, responseData.cancellation_date)
                        Notiflix.Report.info('Already Cancelled', 'This case was already cancelled.', 'OK')
                        return
                    }
                    Report.failure(
                        'Cancellation Failed',
                        responseData?.message || 'An error occurred whilst cancelling the case. Please try again.',
                        'OK',
                    )
                } finally {
                    setIsCancellingClaim(null)
                }
            },
            () => undefined,
            notiflixDanger,
        )
    }

    // E-sign / CFA ----------------------------------------------------------------
    const handleSendEsignLink = async () => {
        if (!clientData?.esign_link) {
            Notiflix.Report.failure('No E-Sign Link', 'This client does not have an e-sign link available.', 'OK')
            return
        }
        setIsSendingEmail(true)
        try {
            await api({
                url: 'send-esign-link',
                data: {
                    agent: authUser?.name,
                    clientData: {
                        first_name: getFirstName(clientData),
                        last_name: getLastName(clientData),
                        email: clientData.email,
                    },
                    esign_link: clientData.esign_link,
                },
            })
            Notiflix.Report.success('E-Sign Link Sent', 'The e-sign link has been sent to the client.', 'OK')
        } catch (error: any) {
            if (isUnauthorised(error)) {
                Notiflix.Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                navigate('/login')
                return
            }
            Report.failure('Send Error', 'An error occurred whilst sending the e-sign link. Please try again.', 'OK')
        } finally {
            setIsSendingEmail(false)
        }
    }

    const handleFindPcpAgreements = async () => {
        if (!clientData?.client_id || !clientData?.origin) {
            Notiflix.Report.failure('Error', 'Client information is missing. Please try again.', 'OK')
            return
        }
        setIsFindingPcpAgreements(true)
        try {
            const response = await api({
                url: 'find-report-pcp-agreements',
                data: { client_id: clientData.client_id, origin: clientData.origin },
            })
            if (response.data.success) {
                Notiflix.Report.success(
                    'PCP Agreements Found',
                    response.data.message ||
                        'PCP agreements were found in the credit report. Refreshing the client record…',
                    'OK',
                    () => void refreshSelectedClient(),
                )
            } else {
                Notiflix.Report.warning(
                    'No PCP Agreements',
                    response.data.message || 'No PCP agreements were found in the credit report.',
                    'OK',
                )
            }
        } catch (error: any) {
            if (isUnauthorised(error)) {
                Notiflix.Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                navigate('/login')
                return
            }
            Report.failure(
                'Search Error',
                'An error occurred whilst searching for PCP agreements. Please try again.',
                'OK',
            )
        } finally {
            setIsFindingPcpAgreements(false)
        }
    }

    const handleRegenCfa = () => {
        if (!clientData) return
        Confirm.show(
            'Regenerate base CFA?',
            `A new base CFA will be generated for ${getFullName(clientData)} (${clientData.email}) in the ${clientData.origin} database.`,
            'Regenerate',
            'Cancel',
            () => void performRegenCfa(),
            () => undefined,
        )
    }

    const performRegenCfa = async () => {
        if (!clientData?.client_id || !clientData?.origin) {
            Notiflix.Report.failure('Error', 'Client information is missing. Please try again.', 'OK')
            return
        }
        setIsRegeneratingCfa(true)
        setCfaUrl(null)
        try {
            const response = await api({
                url: 'regen-cfa',
                data: { introducer: clientData.origin, client_id: clientData.client_id },
            })
            if (response.data.success && response.data.docUrl) {
                setCfaUrl(response.data.docUrl)
                setDocumentsFetchKey((k) => k + 1)
                Notiflix.Report.success(
                    'CFA Regenerated',
                    'The base CFA has been regenerated. A link to view it is shown in the CFA panel.',
                    'OK',
                )
            } else {
                Notiflix.Report.failure(
                    'CFA Regeneration Failed',
                    response.data.error || 'Failed to regenerate the CFA. Please try again.',
                    'OK',
                )
            }
        } catch (error: any) {
            if (isUnauthorised(error)) {
                Notiflix.Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                navigate('/login')
                return
            }
            Report.failure(
                'CFA Regeneration Error',
                'An error occurred whilst regenerating the CFA. Please try again.',
                'OK',
            )
        } finally {
            setIsRegeneratingCfa(false)
        }
    }

    // Render ------------------------------------------------------------------------
    const hasResults = status === 'results'
    const showMatches = hasResults && !clientData && searchResults.length > 0
    const headerMeta =
        lastSearched && hasResults ? (
            <>
                <span>
                    Searched for <strong>{describeCriteria(lastSearched)}</strong>
                </span>
                {searchResults.length > 1 && (
                    <span>
                        <strong>{searchResults.length}</strong> matches
                    </span>
                )}
            </>
        ) : undefined

    return (
        <>
            <PageHeader
                eyebrow="Organisation-wide"
                title="Client Search"
                intro="Find a client across every introducer database and manage their record: CFA status, credit report, claims, contact notes and account actions."
                meta={headerMeta}
                actions={
                    status !== 'idle' ? (
                        <>
                            {clientData && searchResults.length > 1 && (
                                <Button onClick={backToMatches}>Back to matches</Button>
                            )}
                            <Button icon={<FaSearch />} onClick={startNewSearch}>
                                New search
                            </Button>
                        </>
                    ) : undefined
                }
            />

            <SearchPanel
                criteria={criteria}
                errors={errors}
                loading={status === 'loading'}
                open={isSearchOpen}
                onToggle={setIsSearchOpen}
                onChange={updateCriteria}
                onClear={() => {
                    setCriteria(EMPTY_CRITERIA)
                    setErrors({})
                }}
                onSubmit={() => void runSearch(criteria)}
            />

            {status === 'loading' && (
                <EmptyState
                    tone="loading"
                    title="Searching every database…"
                    description="This can take a few seconds while each introducer is checked."
                />
            )}

            {status === 'empty' && (
                <EmptyState
                    title="No client found"
                    description="Nothing matched those details in any introducer database. Check the spelling, try a different identifier, or remove the date of birth."
                    action={
                        <Button size="sm" onClick={() => setIsSearchOpen(true)}>
                            Edit search
                        </Button>
                    }
                />
            )}

            {status === 'error' && (
                <EmptyState
                    tone="error"
                    title="Search failed"
                    description="The search could not be completed. Try again, and contact an administrator if the problem persists."
                    action={
                        <Button size="sm" onClick={() => void runSearch(criteria)}>
                            Retry
                        </Button>
                    }
                />
            )}

            {showMatches && <MatchesTable results={searchResults} onSelect={selectClient} />}

            {clientData && (
                <LoadingOverlay
                    loading={isRefreshingResults || isRegeneratingCfa}
                    text={isRegeneratingCfa ? 'Regenerating CFA…' : 'Refreshing client record…'}
                    className={s.recordHost}
                >
                    <ClientRecord
                        client={clientData}
                        canCancelClaims={canCancelClaims}
                        creditReportUrl={creditReportUrl}
                        isLoadingCreditReport={isLoadingCreditReportUrl}
                        regeneratedCfaUrl={cfaUrl}
                        isSendingEsign={isSendingEmail}
                        isRegeneratingCfa={isRegeneratingCfa}
                        isFindingPcpAgreements={isFindingPcpAgreements}
                        cancellingCaseRef={isCancellingClaim}
                        signatureEvidence={signatureEvidence}
                        onRetrySignatureEvidence={() => setSignatureFetchKey((k) => k + 1)}
                        documents={documents}
                        onRetryDocuments={() => setDocumentsFetchKey((k) => k + 1)}
                        onJumpToActions={canCancelClaims ? jumpToActions : undefined}
                        onSendEsignLink={handleSendEsignLink}
                        onRegenerateCfa={handleRegenCfa}
                        onOpenCreditReportForm={openCreditForm}
                        onFindPcpAgreements={handleFindPcpAgreements}
                        onCancelClaim={handleCancelClaim}
                        notesSection={
                            <CollapsibleSection
                                variant="group"
                                level="h3"
                                title="Contact notes"
                                meta="Calls, emails and letters logged against this client"
                                defaultOpen
                            >
                                <NotesTab
                                    // Remount when the selected client changes so stale notes never linger
                                    key={`${clientData.client_id}-${clientData.origin}`}
                                    clientId={clientData.client_id}
                                    origin={clientData.origin}
                                    clientEmail={clientData.email}
                                />
                            </CollapsibleSection>
                        }
                    />

                    {canCancelClaims && (
                        <div ref={actionsRef}>
                            <CollapsibleSection
                                variant="group"
                                tone="bad"
                                level="h3"
                                title="Account actions"
                                meta="Cancel claims in bulk or erase this client's data — Sentinel users only"
                                open={actionsOpen}
                                onToggle={setActionsOpen}
                            >
                                <ClientActionsTab
                                    // Remount per client so selections / previews never carry over
                                    key={`${clientData.client_id}-${clientData.origin}`}
                                    clientId={clientData.client_id}
                                    origin={clientData.origin}
                                    clientEmail={clientData.email}
                                    clientName={getFullName(clientData)}
                                    onClaimsCancelled={handleActionsClaimsCancelled}
                                    onClientDeleted={handleActionsClientDeleted}
                                />
                            </CollapsibleSection>
                        </div>
                    )}
                </LoadingOverlay>
            )}

            <CreditReportModal
                open={showCreditForm}
                form={creditForm}
                editing={isEditingCreditForm}
                submitting={isRerunningCreditReport}
                hasExistingReport={!!creditReportUrl}
                onClose={closeCreditForm}
                onToggleEditing={setIsEditingCreditForm}
                onChange={(patch) => setCreditForm((prev) => ({ ...prev, ...patch }))}
                onSubmit={confirmRunCreditReport}
            />
        </>
    )
}
